import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import { useNavigate } from "react-router-dom";

const STEPS = [
  {
    number: "1",
    icon: "description",
    title: "Completează formularul",
    description:
      "Spune-ne ce s-a întâmplat și atașează documentele relevante. Datele tale sunt protejate.",
  },
  {
    number: "2",
    icon: "account_balance",
    title: "Identificăm instituția",
    description:
      "Sistemul direcționează sesizarea către instituția competentă, fără ca tu să fie nevoie să știi unde.",
  },
  {
    number: "3",
    icon: "visibility",
    title: "Urmărești statusul",
    description:
      "Primești un cod unic și poți verifica oricând stadiul și răspunsurile oficiale.",
  },
];

// Culori pentru design-ul nou. Iconițele preiau navy-ul închis al PNG-urilor
// de referință din `assets/images/icons/` (gama #0f2c4a / #1a2e4a).
const STEP_BADGE_BG = "#000000";
const STEP_CIRCLE_BG = "#eef1f6";
const STEP_ICON_COLOR = "#1a2e4a";
const CONNECTOR_COLOR = "#c5d1e0";
const EYEBROW_COLOR = "#1565C0";
const TITLE_COLOR = "#0f2c4a";
const DESCRIPTION_COLOR = "#5a6b85";

// Diametre — folosite atât în render cât și pentru poziționarea liniei conectoare
const CIRCLE_DIAMETER = 80; // px
const CIRCLE_RADIUS = CIRCLE_DIAMETER / 2;
const BADGE_DIAMETER = 32; // px

function HowItWorksSection() {
  const navigate = useNavigate();

  return (
    <SoftBox>
      {/* Header + pași — z-index ridicat și fundal alb ca decorul cu mâini
          (care iese în sus din CallToAction) să nu se suprapună peste conținut. */}
      <SoftBox
        sx={{
          position: "relative",
          zIndex: 2,
          bgcolor: "#ffffff",
        }}
      >
      {/* ── Header ── */}
      <SoftBox textAlign="center" mb={{ xs: 5, md: 7 }}>
        <SoftTypography
          variant="overline"
          sx={{
            color: EYEBROW_COLOR,
            fontWeight: 700,
            letterSpacing: "2.5px",
            fontSize: { xs: "0.7rem", md: "0.78rem" },
            display: "block",
            mb: 1,
          }}
        >
          CUM FUNCȚIONEAZĂ
        </SoftTypography>
        <SoftTypography
          sx={{
            color: TITLE_COLOR,
            fontWeight: 700,
            fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.3rem" },
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          3 pași simpli pentru a fi auzit
        </SoftTypography>
      </SoftBox>

      {/* ── Pași ── */}
      <Grid
        container
        spacing={{ xs: 4, md: 0 }}
        sx={{ position: "relative", px: { md: 2, lg: 4 } }}
      >
        {STEPS.map((step, i) => {
          const isLast = i === STEPS.length - 1;
          return (
            <Grid
              item
              xs={12}
              md={4}
              key={step.number}
              sx={{
                position: "relative",
                textAlign: "center",
                px: { xs: 1, md: 2 },
              }}
            >
              {/* Linie punctată conectoare — doar pe desktop, doar între pași
                  (de la marginea dreaptă a cercului curent până la marginea
                  stângă a cercului următor). */}
              {!isLast && (
                <SoftBox
                  aria-hidden="true"
                  sx={{
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    top: `${CIRCLE_RADIUS}px`,
                    left: `calc(50% + ${CIRCLE_RADIUS + 14}px)`,
                    right: `calc(-50% + ${CIRCLE_RADIUS + 14}px)`,
                    borderTop: `2.5px dotted ${CONNECTOR_COLOR}`,
                  }}
                />
              )}

              {/* Cerc + badge cu număr */}
              <SoftBox
                sx={{
                  position: "relative",
                  width: `${CIRCLE_DIAMETER}px`,
                  height: `${CIRCLE_DIAMETER}px`,
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                {/* Cercul mare deschis cu iconul */}
                <SoftBox
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    bgcolor: STEP_CIRCLE_BG,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon
                    sx={{
                      color: `${STEP_ICON_COLOR} !important`,
                      fontSize: "36px !important",
                    }}
                  >
                    {step.icon}
                  </Icon>
                </SoftBox>

                {/* Badge cu numărul — în stânga-sus, ieșind ~35% în diagonală
                    din cerc. Negru pur cu text alb. */}
                <SoftBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    transform: "translate(-35%, -35%)",
                    width: `${BADGE_DIAMETER}px`,
                    height: `${BADGE_DIAMETER}px`,
                    borderRadius: "50%",
                    bgcolor: STEP_BADGE_BG,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    border: "3px solid #fff",
                    zIndex: 2,
                  }}
                >
                  <SoftTypography
                    sx={{
                      color: "#fff !important",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      lineHeight: 1,
                    }}
                  >
                    {step.number}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>

              {/* Titlu */}
              <SoftTypography
                sx={{
                  color: TITLE_COLOR,
                  fontWeight: 700,
                  fontSize: { xs: "1.05rem", md: "1.15rem" },
                  mb: 1,
                }}
              >
                {step.title}
              </SoftTypography>

              {/* Descriere */}
              <SoftTypography
                sx={{
                  color: DESCRIPTION_COLOR,
                  fontSize: { xs: "0.88rem", md: "0.92rem" },
                  lineHeight: 1.6,
                  maxWidth: "280px",
                  mx: "auto",
                }}
              >
                {step.description}
              </SoftTypography>
            </Grid>
          );
        })}
      </Grid>
      </SoftBox>

      {/* ── Banner Ghid Complet ── */}
      <SoftBox
        mt={{ xs: 6, md: 12 }}
        mb={{ xs: 4, md: 6 }}
        sx={{
          background: "linear-gradient(135deg, #061D36 0%, #0f2c4a 100%)",
          borderRadius: "12px",
          p: { xs: 3, md: 4 },
          position: "relative",
          // z-index ridicat ca să acopere decorul cu mâini care iese în sus
          // din banner-ul CallToAction de mai jos.
          zIndex: 2,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 8px 28px rgba(6, 29, 54, 0.22)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.04)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-30%",
            left: "-5%",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.04)",
          },
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <SoftBox position="relative" zIndex={2}>
              <SoftBox display="flex" alignItems="center" mb={1.5}>
                <Icon sx={{ color: "#FFC107 !important", fontSize: "28px", mr: 1.5 }}>
                  menu_book
                </Icon>
                <SoftTypography variant="h5" color="white" fontWeight="bold">
                  Ghid complet de utilizare
                </SoftTypography>
              </SoftBox>
              <SoftTypography variant="body2" color="white" opacity={0.95} lineHeight={1.6}>
                Află cum să completezi corect formularul, cum să urmărești statusul sesizării tale și
                cum funcționează procesul de raportare pas cu pas.
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <SoftBox position="relative" zIndex={2} textAlign={{ xs: "left", md: "right" }}>
              <SoftButton
                variant="contained"
                color="white"
                size="medium"
                onClick={() => navigate("/ghid")}
                sx={{
                  color: "#061D36",
                  fontWeight: "bold",
                  px: 3,
                  py: 1.2,
                  fontSize: "0.95rem",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.18)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Icon sx={{ mr: 1 }}>arrow_forward</Icon>
                Vezi ghidul complet
              </SoftButton>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

export default HowItWorksSection;
